import React, { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type SxProps, type Theme } from '@mui/material';
import { mergeSx } from 'merge-sx';

import ChipSelect, { ChipSelectProps } from '@/components/fields/ChipSelect';
import withFormControl from '@/hoc/withFormControl';

interface ButtonGroupToggleProps extends Omit<ChipSelectProps, 'value' | 'onChange'> {
  id?: string;
  name?: string;
  value?: boolean;
  onChange?: (event: boolean) => void;
  sx?: SxProps<Theme>;
  prefix?: string;
  ns?: string;
  disabled?: boolean;
}

const YES_VALUE = true;
const NO_VALUE = false;

const ButtonGroupToggleField: FC<ButtonGroupToggleProps> = ({
  id,
  value,
  name,
  onChange,
  sx,
  prefix = 'toggle',
  ns = 'common',
  disabled = false,
  ...props
}) => {
  const { t } = useTranslation(ns);

  const chipOptions = useMemo(
    () => [
      { label: t(`${prefix}.${YES_VALUE}`), value: YES_VALUE.toString() },
      { label: t(`${prefix}.${NO_VALUE}`), value: NO_VALUE.toString() }
    ],
    [t, prefix]
  );

  const selectValue = useMemo(() => (value ? YES_VALUE : NO_VALUE).toString(), [value]);

  const onToggle = useCallback(() => {
    onChange?.(!value);
  }, [onChange, value]);

  return (
    <ChipSelect
      id={id}
      name={name}
      value={selectValue}
      onChange={onToggle}
      options={chipOptions}
      sx={mergeSx(
        {
          filter: disabled ? 'grayscale(100%)' : 'none'
        },
        sx
      )}
      color="primary"
      disabled={disabled}
      {...props}
    />
  );
};

const ButtonGroupToggle = withFormControl<boolean, boolean, ButtonGroupToggleProps>(ButtonGroupToggleField);

export default ButtonGroupToggle;
